import { inject, injectable } from 'inversify'
import { action, makeObservable, observable } from 'mobx'
import { Types } from '@core/ioc/types'
import type { AppLifecycle } from '@core/interfaces/app-lifecycle'
import type { ViewportGateway } from '@core/services/viewport.gateway'
import { ViewportConfig } from './viewport.config'

/**
 * Source of truth for whether the app is currently on a narrow (phone-width)
 * viewport.
 *
 * A preference-like value that always resolves — seeded `false` and corrected
 * on `initialize()`, so nothing has to render against `null`. Presenters read
 * `isNarrowPm` to fold the phone case into an existing display decision (e.g.
 * a list Presenter's `compact` VM field) rather than each Component reaching
 * for `window.innerWidth` (CI-8).
 *
 * Same shape as ConnectivityRepository: an AppLifecycle participant that opens
 * one app-lifetime subscription in `initialize()`.
 */
@injectable()
export class NarrowViewportRepository implements AppLifecycle {
  isNarrowPm = false

  @inject(Types.IViewportGateway)
  private viewportGateway!: ViewportGateway

  @inject(ViewportConfig)
  private viewportConfig!: ViewportConfig

  private watchesViewport = false

  constructor() {
    makeObservable(this, {
      isNarrowPm: observable,
      setNarrow: action,
    })
  }

  // Called once from AppPresenter.load() — property-injected deps aren't
  // populated during construction, same pattern as the other init()s. Safe to
  // call twice: the subscription is guarded, the read just re-syncs.
  initialize = action(() => {
    this.isNarrowPm = this.viewportGateway.matches(this.viewportConfig.narrowQuery)
    if (this.watchesViewport) return

    this.viewportGateway.onChange(this.viewportConfig.narrowQuery, (matches) => this.setNarrow(matches))
    this.watchesViewport = true
  })

  setNarrow(narrow: boolean) {
    this.isNarrowPm = narrow
  }
}
